import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { TaskBoard as KTaskBoard, TaskBoardChangeEvent, TaskBoardColumnModel, TaskBoardPriority, TaskBoardTaskModel, TaskBoardToolbar } from '@progress/kendo-react-taskboard'
import React, { useEffect, useMemo, useState } from 'react'
import { Column } from "./components/Column";
import { Card } from "./components/Card";
import './TaskBoard.scss';
import { Filing } from '../../../utilities/interfaces/Filing';
import CardHelper from './CardHelper';
import { WorkQueueColors } from '../../../utilities/enums/WorkQueueColors';
import { filingWorkflowHistoryApi } from '../../../utilities/api';

interface ITaskBoard {
  columns: TaskBoardColumnModel[];
  filings: Filing[];
}

const priorities: TaskBoardPriority[] = [
  { priority: "New ", color: WorkQueueColors.Blue },
  { priority: "Public Hearing Pending", color: WorkQueueColors.BlueGreen },
  { priority: "AG Pending", color: WorkQueueColors.Green },
  { priority: "GPRO Pending", color: WorkQueueColors.Yellow },
  { priority: "SOS Pending", color: WorkQueueColors.Orange },
  { priority: "Ready For Promulgation", color: WorkQueueColors.Orange }
];

/**
 * This taskboard component accepts in the taskboard columns and filings
 * The filings are then parsed using the card helper to turn them into "task"
 * objects to be consumed by the taskboard as "cards"
 * NOTE: This component is not generic and is built specifically for filing data
 * @param columns: TaskBoardColumnModel[] - array of column data
 * @param filings: Filing[] - Array of filing data
 * @returns React.Node - Kendo Taskboard component
 * @example
 * TaskBoardColumnModel { id: 1, title: 'Submitted to OAR', status: 'urgent'},
 * <CustomTaskboard filings={Filing[]} columns={columns}/>
 */
const Taskboard = (props: ITaskBoard) => {
  const [filter, setFilter] = useState<string>("");
  const [columnsData] = useState<TaskBoardColumnModel[]>(props.columns);
  const [taskData, setTaskData] = useState<TaskBoardTaskModel[]>([]);
  const [histories, setHistories] = useState<any>([]);

  useEffect(() => {
    if (histories.length < 1) {
      fetchHistories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchHistories = async () => {
    let newHistories = await filingWorkflowHistoryApi.getAll();
    setHistories(newHistories);
  }

  useEffect(() => {
    if (taskData.length < 1 && histories.length > 1) {
      let cardHelper = new CardHelper();
      let cards = cardHelper.createCards(props.filings, histories);
      setTaskData(cards)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filings, histories])


  // this is not currently used but is required for the Kendo taskboard component
  const onChangeHandler = React.useCallback((event: TaskBoardChangeEvent) => {
    if (event.type === "column") {
      // setColumnsData(event.data as TaskBoardColumnModel[]);
    } else {
      // New Task has been added.
      if (event.item && event.item.id === undefined) {
        event.item.id = event.data.length + 1;
      }
      setTaskData(event.data as TaskBoardTaskModel[]);
    }
  }, []);

  const resultTasks = useMemo(() => {
    const filterDesc: CompositeFilterDescriptor = {
      logic: "and",
      filters: [{ field: "title", operator: "contains", value: filter }],
    };
    return filter ? filterBy(taskData, filterDesc) : taskData;
  }, [filter, taskData]);

  const onSearchChange = React.useCallback((event: InputChangeEvent) => {
    setFilter(event.value);
  }, []);

  return (
    <KTaskBoard
      columnData={columnsData}
      taskData={resultTasks}
      priorities={priorities}
      onChange={onChangeHandler}
      column={Column}
      card={Card}
      style={{ height: "90svh", width: "95svw" }}
      tabIndex={0}
    >
      <TaskBoardToolbar>
        <Input
          placeholder="Search..."
          onChange={onSearchChange}
          value={filter}
        />
      </TaskBoardToolbar>
    </KTaskBoard>
  )
}

export default Taskboard